import { useEffect, useRef, useState } from 'react';
import { getViewModel$ } from './viewModel$';
import { PersonalSetting, ViewIds } from '@ardoq/api-types';
import { onViewSettingsUpdate } from '../onViewSettingsUpdate';
import { SettingsBar, settingsBarConsts } from '@ardoq/settings-bar';
import { getRightMenuConfig } from './getRightMenuConfig';
import { getLeftMenuConfig } from './getLeftMenuConfig';
import { ChartWrapper, ViewContainer } from './atoms';
import { useResizeObserver } from '@ardoq/hooks';
import { SpiderChart } from './view/SpiderChart';
import { useViewLegendSubscription } from 'views/viewLegend/useViewLegendSubscription';
import { ViewLegend } from '@ardoq/view-legend';
import { getViewLegendLabels } from 'viewLegend/getViewLegendLabels';
import { ViewLegendContainer } from '@ardoq/graph';
import { isPresentationMode } from 'env';
import { EmptyState as EmptyStateBase } from './EmptyState';
import { noop } from 'lodash';
import { ErrorInfoBox } from '@ardoq/error-info-box';
import { usePersonalSettingToggle } from 'streams/currentUser/usePersonalSettingToggle';
import { SpiderChartStreamedProps } from './types';
import { simpleNotifier } from '@ardoq/dropdown-menu';
import { connectInstanceWithProps } from 'streams/utils/connectInstanceWithProps';

const VIEW_ID = ViewIds.SPIDER;

const SpiderChartView = ({
  viewSettings,
  viewModel,
  errorInfoBoxLabels,
  i18nLocale,
}: SpiderChartStreamedProps) => {
  const {
    chartPoints,
    legendRows,
    selectedFields,
    componentsCount,
    errors,
    numericFields,
    sideways,
    focusedComponentId,
  } = viewModel;
  const containerElement = useRef<HTMLDivElement>(null);
  const chartWrapperElement = useRef<HTMLDivElement>(null);
  const openSelectFieldsViewSetting = useRef(simpleNotifier());

  const { width: containerWidth = 0, height: containerHeight = 0 } =
    useResizeObserver(containerElement);

  useEffect(() => {
    const scrollWidth = chartWrapperElement.current?.scrollWidth ?? 0;
    const clientWidth = chartWrapperElement.current?.clientWidth ?? 0;
    const scrollCenterPosition = (scrollWidth - clientWidth) / 2;

    chartWrapperElement.current?.scrollTo(scrollCenterPosition, 0);
  }, [containerWidth]);

  const [clearedErrors, setClearedErrors] = useState(false);
  const [neverShowAgain, toggleNeverShowAgain] = usePersonalSettingToggle(
    PersonalSetting.NEVER_SHOW_CLONE_INFO_AGAIN_SPIDER
  );

  const noNumericFields = !numericFields.length;
  const noSelectedFields = !selectedFields.length;
  const notEnoughComponents =
    typeof componentsCount === 'number' && componentsCount < 1;

  const isEmptyView =
    notEnoughComponents || noNumericFields || noSelectedFields;

  return (
    <>
      {!isPresentationMode() && (
        <SettingsBar
          viewId={VIEW_ID}
          i18nLocale={i18nLocale}
          leftMenu={getLeftMenuConfig(
            VIEW_ID,
            viewSettings,
            sideways,
            numericFields,
            selectedFields,
            openSelectFieldsViewSetting.current,
            i18nLocale
          )}
          rightMenu={getRightMenuConfig({
            viewId: VIEW_ID,
            getContainer: () => containerElement?.current,
            viewSettings,
            isEmptyView,
            onViewSettingsUpdate,
            i18nLocale,
          })}
        />
      )}

      <ViewContainer ref={containerElement}>
        {isEmptyView ? (
          <EmptyStateBase
            noNumberFields={noNumericFields}
            noSelectedFields={noSelectedFields}
            notEnoughComponents={notEnoughComponents}
            allDescendantsIncluded={viewSettings.includeAllDescendants}
            openSelectFieldsViewSetting={openSelectFieldsViewSetting.current}
          />
        ) : (
          <ChartWrapper ref={chartWrapperElement}>
            <SpiderChart
              chartPoints={chartPoints}
              containerWidth={containerWidth}
              containerHeight={containerHeight}
              focusedComponentId={focusedComponentId}
            />
          </ChartWrapper>
        )}

        <ErrorInfoBox
          errors={clearedErrors ? [] : errors}
          hasClones={false}
          clearHasClones={noop}
          isShowNeverAgainSet={neverShowAgain}
          isPresentationMode={isPresentationMode()}
          clearErrors={() => setClearedErrors(true)}
          toggleNeverShowAgain={toggleNeverShowAgain}
          {...errorInfoBoxLabels}
        />

        <ViewLegendContainer
          heightOffset={useViewLegendSubscription()}
          visible={
            !isEmptyView && viewSettings[settingsBarConsts.IS_LEGEND_ACTIVE]
          }
        >
          <ViewLegend
            activeDiffMode={null}
            componentTypes={[]}
            displayShapesInsteadOfIcons={true}
            additionalRows={legendRows}
            labels={getViewLegendLabels(i18nLocale)}
          />
        </ViewLegendContainer>
      </ViewContainer>
    </>
  );
};

const ConnectedSpiderChart = connectInstanceWithProps(
  SpiderChartView,
  getViewModel$
);

export { ConnectedSpiderChart };
